import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { fetchCached, useCached } from '../lib/cache'
import { loadBrands, loadConsignors, loadCustomers, loadEmployees, loadMode, loadTaxSettings, nextIntakeNo } from '../lib/api'
import type { Consignor, Customer, Employee, IntakeDraft, SaleDraft, TaxSettings, TradeDraft } from '../lib/types'
import { emptyDraft, emptySale, emptyTrade, TAX_DEFAULT } from '../lib/types'

interface Store {
  employees: Employee[]
  consignors: Consignor[]
  customers: Customer[]
  brands: string[]
  intakeNo: number
  draft: IntakeDraft
  setDraft: (patch: Partial<IntakeDraft>) => void
  resetDraft: () => void
  sale: SaleDraft
  setSale: (patch: Partial<SaleDraft> | ((s: SaleDraft) => Partial<SaleDraft>)) => void
  resetSale: () => void
  trade: TradeDraft
  setTrade: (patch: Partial<TradeDraft> | ((t: TradeDraft) => Partial<TradeDraft>)) => void
  resetTrade: () => void
  refresh: () => Promise<void>
  online: boolean
  /** true once the first load of the lists has finished (or failed), so a screen can tell empty from not-loaded-yet */
  ready: boolean
  mode: { rehearsal: boolean; label: string }
  tax: TaxSettings
}

const Ctx = createContext<Store | null>(null)
const NONE_E: Employee[] = [], NONE_C: Consignor[] = [], NONE_CU: Customer[] = [], NONE_B: string[] = []
const MODE0 = { rehearsal: false, label: '' }

export function StoreProvider({ children }: { children: ReactNode }) {
  // The lists come from the read cache (lib/cache.ts): on an unlock or a relaunch the screens have them at once, and every
  // time the shell opens they are re-read from the server in the background, as they were before.
  const E = useCached<Employee[]>('employees', loadEmployees, { staleMs: 60_000 })
  const C = useCached<Consignor[]>('consignors', loadConsignors, { staleMs: 60_000 })
  const CU = useCached<Customer[]>('customers', loadCustomers, { staleMs: 60_000 })
  const B = useCached<string[]>('brands', loadBrands, { staleMs: 60_000 })
  const N = useCached<number>('intakeNo', nextIntakeNo, { staleMs: 0 })
  const M = useCached<{ rehearsal: boolean; label: string }>('mode', loadMode, { staleMs: 60_000 })
  const T = useCached<TaxSettings>('tax', loadTaxSettings, { staleMs: 60_000 })
  const employees = E.data ?? NONE_E, consignors = C.data ?? NONE_C, customers = CU.data ?? NONE_CU, brands = B.data ?? NONE_B
  const intakeNo = N.data ?? 0
  const mode = M.data ?? MODE0
  const tax = T.data ?? TAX_DEFAULT
  const [draft, setDraftState] = useState<IntakeDraft>(emptyDraft())
  const [sale, setSaleState] = useState<SaleDraft>(emptySale())
  const [trade, setTradeState] = useState<TradeDraft>(emptyTrade())
  const [online, setOnline] = useState(navigator.onLine)
  const [settled, setSettled] = useState(false)

  /** Re-read every list from the server; resolves when the lists are in (callers await it after a write, as before). */
  const refresh = useCallback(async () => {
    await Promise.all([fetchCached('employees', loadEmployees), fetchCached('consignors', loadConsignors), fetchCached('customers', loadCustomers), fetchCached('brands', loadBrands), fetchCached('intakeNo', nextIntakeNo)])
    fetchCached('mode', loadMode).catch(() => {})
    fetchCached('tax', loadTaxSettings).catch(() => {})
  }, [])

  // first load of this shell: settled once every list has come back or failed (the cache may already have them all)
  useEffect(() => { Promise.allSettled([E.refresh(), C.refresh(), CU.refresh(), B.refresh()]).finally(() => setSettled(true)) }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const ready = settled || (!!E.data && !!C.data && !!CU.data && !!B.data)
  // 16 Oct (Tim): a short wifi drop at the first load left the intake number at #0000 and the lists empty until a relaunch.
  // While anything is still missing, try again every 5 seconds; when the wifi comes back or the app is brought to the front,
  // re-read everything.
  const missing = !N.data || !E.data || !C.data
  useEffect(() => {
    if (!missing) return
    const t = window.setInterval(() => { refresh().catch(() => {}) }, 5000)
    return () => window.clearInterval(t)
  }, [missing, refresh])
  useEffect(() => {
    const again = () => { if (document.visibilityState === 'visible') refresh().catch(() => {}) }
    window.addEventListener('online', again); document.addEventListener('visibilitychange', again)
    return () => { window.removeEventListener('online', again); document.removeEventListener('visibilitychange', again) }
  }, [refresh])
  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false)
    window.addEventListener('online', on); window.addEventListener('offline', off)
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off) }
  }, [])

  const value = useMemo<Store>(() => ({
    employees, consignors, customers, brands, intakeNo, draft, sale, trade, online, ready, mode, tax, refresh,
    setDraft: (patch) => setDraftState((d) => ({ ...d, ...patch })),
    resetDraft: () => setDraftState(emptyDraft()),
    setSale: (patch) => setSaleState((cur) => ({ ...cur, ...(typeof patch === 'function' ? patch(cur) : patch) })),
    resetSale: () => setSaleState(emptySale()),
    setTrade: (patch) => setTradeState((cur) => ({ ...cur, ...(typeof patch === 'function' ? patch(cur) : patch) })),
    resetTrade: () => setTradeState(emptyTrade()),
  }), [employees, consignors, customers, brands, intakeNo, draft, sale, trade, online, ready, mode, tax, refresh])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useStore() {
  const s = useContext(Ctx)
  if (!s) throw new Error('StoreProvider missing')
  return s
}
